import React, { useState } from 'react';
import { useHousingTenureData } from '@/hooks/useHousingTenureData';
import { usePeerHousingTenureData } from '@/hooks/usePeerHousingTenureData';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/shared/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/shared/tooltip";
import { formatPlaceName, cn } from '@/lib/utils';
import { ComparisonBarChart } from './ComparisonBarChart';
import { Place } from '@/types';

interface Props {
  place: string | null;
  placeFips: string | null;
  peers: Place[];
  labelFontSize?: number;
}

const HousingTenureCard: React.FC<Props> = ({ place, placeFips, peers, labelFontSize }) => {
  const { data: placeData, loading, error } = useHousingTenureData(place, placeFips);
  const { data: peerData, loading: peersLoading } = usePeerHousingTenureData(peers);
  const [isTitleHovered, setIsTitleHovered] = useState(false);

  const calculateMedianAge = (record: any): number => {
    if (!record) return 0;
    const timePeriods = [
      { midpoint: 2019, units: record.units_built_2014_or_later },
      { midpoint: 2012, units: record.units_built_between_2010_and_2013 },
      { midpoint: 2005, units: record.units_built_between_2000_and_2009 },
      { midpoint: 1995, units: record.units_built_between_1990_and_1999 },
      { midpoint: 1985, units: record.units_built_between_1980_and_1989 },
      { midpoint: 1975, units: record.units_built_between_1970_and_1979 },
      { midpoint: 1965, units: record.units_built_between_1960_and_1969 },
      { midpoint: 1955, units: record.units_built_between_1950_and_1959 },
      { midpoint: 1945, units: record.units_built_between_1940_and_1949 },
      { midpoint: 1925, units: record.units_built_1939_or_earlier },
    ];
    let totalUnits = 0;
    let weightedSum = 0;
    timePeriods.forEach(period => {
      const units = Number(period.units) || 0;
      totalUnits += units;
      weightedSum += units * (new Date().getFullYear() - period.midpoint);
    });
    return totalUnits > 0 ? weightedSum / totalUnits : 0;
  };

  const getRankSuffix = (rank: number) => {
    if (rank > 10 && rank < 20) return 'th';
    const lastDigit = rank % 10;
    switch (lastDigit) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  const chartData = React.useMemo(() => {
    if (!placeData.length || !peerData) return [];
    
    const mostRecentPlaceData = placeData[placeData.length - 1];

    const allCities = peers
      .filter(peer => peer.place_fips.toString() !== placeFips)
      .map(peer => {
        const peerRecords = peerData[peer.place_fips];
        const latestPeerRecord = peerRecords ? peerRecords[0] : null;
        return {
          name: peer.name,
          value: parseFloat(calculateMedianAge(latestPeerRecord).toFixed(2)),
          isSelected: false,
        };
      });

    if (mostRecentPlaceData) {
      allCities.push({
        name: mostRecentPlaceData.Place,
        value: parseFloat(calculateMedianAge(mostRecentPlaceData).toFixed(2)),
        isSelected: true,
      });
    }

    return allCities.sort((a, b) => a.value - b.value); // Sort ascending, lower age is better
  }, [placeData, peerData, peers, placeFips]);

  const calculateRank = () => {
    if (!place) return null;
    const rank = chartData.findIndex(item => item.isSelected) + 1;
    return rank > 0 ? `${rank}${getRankSuffix(rank)}` : null;
  };
  
  const reversedChartData = React.useMemo(() => [...chartData].reverse(), [chartData]);
  const lastYear = placeData.length > 0 ? placeData[placeData.length - 1]?.year : null;

  if (!place || !placeFips) {
    return (
      <Card className="p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Housing Age</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Please select a place to view housing age data.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="flex flex-col p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Housing Age for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div>Loading housing age data...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="flex flex-col p-4 border border-[#34a489]">
        <CardHeader>
          <CardTitle className="text-xl font-semibold">Housing Age for {formatPlaceName(place)}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <TooltipProvider>
      <Card className="flex flex-col p-4 border border-[#ca0270] relative w-full max-w-[600px]">
        <div className="absolute top-4 left-4 bg-[#908ddb] text-white text-sm font-semibold px-3 py-1 rounded-full">
          Housing Stock
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="absolute top-4 right-4 bg-[#ca0270] text-white text-sm font-semibold px-3 py-1 rounded-full cursor-help">
              {peersLoading ? 'Calculating...' : `Peer Ranking: ${calculateRank() || 'N/A'}`}
            </div>
          </TooltipTrigger>
          <TooltipContent className="opacity-100 z-50 bg-white text-black" sideOffset={8}>
            <p>Ranked by: Lowest (Newest) Median Housing Age </p>
          </TooltipContent>
        </Tooltip>
        <CardHeader className="pt-8 pb-0">
          <CardTitle
            className="text-xl lg:text-2xl font-bold"
          >
            Median Housing Age
          </CardTitle>
          <p className="text-sm text-gray-500 min-h-[4rem] pt-1">Median age of the housing stock based on year of construction, data from {lastYear}</p>
        </CardHeader>
        <CardContent className="relative p-0">
          <ComparisonBarChart
            data={reversedChartData}
            yAxisLabel="Median Housing Age (Years)"
            tooltipFormatter={(value) => `${value.toFixed(2)} years`}
            selectedColor="#f6b90d"
            height={200}
            labelFontSize={labelFontSize}
          />
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};

export default HousingTenureCard; 